import * as React from 'react'

/**
 * Runs `reset` on the render where the drawer goes from closed to open.
 *
 * The previous value is held in STATE rather than a ref, and that is the whole point: React
 * discards a render it does not commit, and a ref written during such a render keeps its new
 * value while the state updates beside it are thrown away. The drawer would then open with
 * the previous session's form and template still selected, and - because the ref now says it
 * has already handled this transition - never clear them.
 *
 * Adjusted during render rather than in an effect, deliberately, and the same way
 * {@link useChosenTemplateGate} does it: an effect runs after commit, so the first opened
 * frame would paint the previous session's choice before clearing it. React discards this
 * render and re-runs it with the new state before touching the DOM.
 *
 * `reset` must only call state setters - it is invoked during render.
 */
export function useResetOnOpen(isOpen: boolean, reset: () => void): void {
  const [wasOpen, setWasOpen] = React.useState(false)

  if (isOpen !== wasOpen) {
    setWasOpen(isOpen)
    if (isOpen) {
      reset()
    }
  }
}
